import {
  Component,
  ElementRef,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  signal,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FriendsCrudService } from '../service/friends-crud.service';
import { FriendEvent, IFriend, IFriendsResponse } from '../model/friends.model';
import { FriendCardComponent } from '../friend-card/friend-card.component';
import { IconComponent } from 'app/shared/icon/icon.component';
import { ButtonComponent } from 'app/shared/button/button.component';
import { NgIconComponent, provideIcons, provideNgIconLoader, withCaching } from '@ng-icons/core';
import { heroArrowLongLeft } from '@ng-icons/heroicons/outline';
import { debounceTime, exhaustMap, filter, finalize, map, of, Subscription, tap } from 'rxjs';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MIN_QUERY_LENGTH } from '../friends.const';
import { UserCardComponent } from '../user-card/user-card.component';
import { HttpClient } from '@angular/common/http';

@Component({
  selector: 'app-friends-list',
  standalone: true,
  imports: [
    FriendCardComponent,
    IconComponent,
    ButtonComponent,
    ReactiveFormsModule,
    UserCardComponent,
    NgIconComponent,
  ],
  templateUrl: './friends-list.component.html',
  styleUrls: ['./friends-list.component.scss'],
  providers: [
    provideIcons({ heroArrowLongLeft }),
    provideNgIconLoader((name) => {
      const http = inject(HttpClient);
      return http.get(`/assets/icons/${name}.svg`, { responseType: 'text' });
    }, withCaching()),
  ],
})
export class FriendsListComponent implements OnInit, OnChanges, OnDestroy {
  @Input() tabUpdated!: number;
  @ViewChild('scrollUsersContainer', { static: false }) scrollUsersContainer!: ElementRef;
  @ViewChild('scrollFriendsContainer', { static: false }) scrollFriendsContainer!: ElementRef;

  public searchPeople = new FormControl('', [Validators.minLength(MIN_QUERY_LENGTH)]);
  public searchFriend = new FormControl('', [Validators.minLength(MIN_QUERY_LENGTH)]);

  friendsService = inject(FriendsCrudService);
  friendsList = signal<IFriend[]>([]);
  usersList = signal<IFriend[]>([]);

  addFriendsScreen = signal(false);

  private subscriptions = new Subscription();

  private isUsersFetching = false;
  private allUsersLoaded = false;

  private isFriendsFetching = false;
  private allFriendsLoaded = false;

  protected friendsTotal = signal(0);
  protected usersTotal = signal(0);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tabUpdated'].currentValue === 0) {
      this.addFriendsScreen.set(false);
      this.getInitialFriends();
    }
  }

  ngOnInit() {
    this.getInitialFriends();

    this.subscriptions.add(
      this.searchPeople.valueChanges
        .pipe(
          filter((value): value is string => value !== null),
          map((value: string) => value.trim().toLowerCase()),
          debounceTime(500),
          filter((value: string) => value.length >= MIN_QUERY_LENGTH),
        )
        .subscribe((query) => {
          this.usersList.set([]);
          this.allUsersLoaded = false;

          this.loadMoreUsers(query);
        }),
    );

    this.subscriptions.add(
      this.searchFriend.valueChanges
        .pipe(
          filter((value): value is string => value !== null),
          map((value: string) => value.trim().toLowerCase()),
          debounceTime(500),
          filter((value: string) => value.length >= MIN_QUERY_LENGTH || !value.length),
        )
        .subscribe((query) => {
          // Reset friends list before filtering
          this.friendsList.set([]);
          this.allFriendsLoaded = false;

          this.loadMoreFriends(query);
        }),
    );
  }

  getInitialFriends() {
    this.subscriptions.add(
      this.friendsService
        .getFriends('', 50)
        .pipe(
          tap(() => (this.isFriendsFetching = true)),
          finalize(() => (this.isFriendsFetching = false)),
        )
        .subscribe((friends: IFriendsResponse) => {
          if (friends.data.length) {
            this.friendsList.set(friends.data);
            this.allFriendsLoaded = friends.total <= this.friendsList().length;
            this.friendsTotal.set(friends.total);
            this.friendsService.setFriendsTotal(friends.total);
          }
        }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  onFriendsScroll() {
    const container = this.scrollFriendsContainer.nativeElement;
    const bottomReached = container.scrollTop + container.clientHeight >= container.scrollHeight - 200;

    if (bottomReached && !this.allFriendsLoaded && !this.isFriendsFetching) {
      this.loadMoreFriends(this.searchFriend.value || '');
    }
  }

  onUsersScroll() {
    const container = this.scrollUsersContainer.nativeElement;
    const bottomReached = container.scrollTop + container.clientHeight >= container.scrollHeight - 200; // 50px buffer

    if (bottomReached && !this.allUsersLoaded && !this.isUsersFetching) {
      this.loadMoreUsers(this.searchPeople.value || '');
    }
  }

  openAddFriendScreen() {
    this.searchPeople.setValue('');
    this.addFriendsScreen.set(true);
    this.usersList.set([]);
    this.allUsersLoaded = false;
  }

  backToFriendsList() {
    this.searchFriend.setValue('');
    this.addFriendsScreen.set(false);
    this.friendsList.set([]);
    this.allFriendsLoaded = false;
    this.loadMoreFriends('');
  }

  onEvent(event: { type: FriendEvent; friendId: number }) {
    const list = this.friendsList();

    if (event.type === FriendEvent.ShareTo || event.type === FriendEvent.StopSharingFrom) {
      this.friendsService.getFriendsById(event.friendId).subscribe((response: IFriendsResponse) => {
        if (response && response.data && response.data.length) {
          const updatedFriend: IFriend = response.data[0];
          const updatedFriends = this.friendsList().map((friend) =>
            friend.id === updatedFriend.id ? updatedFriend : friend,
          );
          this.friendsList.set(updatedFriends);
        }
      });
    }

    if (event.type === FriendEvent.Removed) {
      this.friendsList.set(list.filter((f) => f.id !== event.friendId));
      this.friendsTotal.set(this.friendsTotal() - 1);
      this.friendsService.setFriendsTotal(this.friendsService.getFriendsTotal() - 1)
    }
  }

  trackById(index: number, item: { id: number }) {
    return item.id;
  }

  private loadMoreUsers(query: string): void {
    if (this.allUsersLoaded || this.isUsersFetching) return;

    this.isUsersFetching = true;

    this.friendsService
      .getUsers(query, 50, this.usersList().length)
      .pipe(
        exhaustMap((usersResponse) => {
          if (usersResponse.data.length) {
            this.usersList.update((list) => [...list, ...usersResponse.data]);
            this.allUsersLoaded = usersResponse.total <= this.usersList().length;
            // this.usersTotal.set(usersResponse.total);
          }
          return of(null);
        }),
        finalize(() => (this.isUsersFetching = false)),
      )
      .subscribe();
  }

  private loadMoreFriends(query: string): void {
    if (this.allFriendsLoaded || this.isFriendsFetching) return;

    this.isFriendsFetching = true;

    this.friendsService
      .getFriends(query, 50, this.friendsList().length)
      .pipe(
        exhaustMap((friendsResponse) => {
          if (friendsResponse.data.length) {
            this.friendsList.update((list) => [...list, ...friendsResponse.data]);
            this.allFriendsLoaded = friendsResponse.total <= this.friendsList().length;
            this.friendsTotal.set(friendsResponse.total);
            this.friendsService.setFriendsTotal(friendsResponse.total);
          }
          return of(null);
        }),
        finalize(() => (this.isFriendsFetching = false)),
      )
      .subscribe();
  }
}
